/* ============================================================
   Location grouping for the v2 scan map + group detail drawer.
   Co-located signals (same resolved centroid) collapse into one
   `LocationGroup` so the map renders a single marker and the
   group drawer can show every signal at that location.
   Pure + framework-free so the map and the body can both call it.
   ============================================================ */
import type { BiothreatSignal } from "@phc/common/gen/threatAnalyticsGen"
import type { PrecomputedTab } from "../types"
import {
  classifyImpact,
  getDiseaseLabel,
  getImpact,
  getLocationLabel,
  getNwssStatus,
} from "./biothreatV2Signal"
import { resolveSignalLatLon } from "./biothreatV2Geo"
import { V2_SEVERITY_COLOR, V2_STATUS_COLOR } from "./biothreatV2Tokens"

/** Signals sharing a resolved centroid, rendered as one marker. */
export interface LocationGroup {
  /** "lat,lon" composite key — stable id for URL selection. */
  key: string
  lat: number
  lon: number
  color: string
  count: number
  /** Highest-weight signal in the group — opened on a single-signal click. */
  lead: BiothreatSignal
  locationLabel: string
  /** Every signal at this location, highest-weight first. */
  signals: BiothreatSignal[]
  signalIds: string[]
  /** Disease breakdown at this location, most clusters first. */
  diseases: { label: string; count: number }[]
}

/** Marker color: NWSS by status, GDELT by severity tier. */
export const markerColor = (
  row: BiothreatSignal,
  kind: PrecomputedTab
): string => {
  if (kind === "nwss") {
    const status = getNwssStatus(row)
    return status ? V2_STATUS_COLOR[status] : V2_SEVERITY_COLOR.low
  }
  return V2_SEVERITY_COLOR[classifyImpact(getImpact(row))]
}

/** Lead-selection weight: impact for GDELT, detection count for NWSS. */
export const signalWeight = (
  row: BiothreatSignal,
  kind: PrecomputedTab
): number =>
  kind === "nwss" ? (row.nwss_meta?.n_detections ?? 0) : getImpact(row)

/** Disease breakdown for a set of signals, most clusters first. */
export const summarizeDiseases = (
  signals: BiothreatSignal[]
): { label: string; count: number }[] => {
  const diseaseCounts = new Map<string, number>()
  for (const signal of signals) {
    const label = getDiseaseLabel(signal)
    diseaseCounts.set(label, (diseaseCounts.get(label) ?? 0) + 1)
  }
  return Array.from(diseaseCounts.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
}

/**
 * Aggregate signals by resolved centroid so co-located clusters (e.g. every
 * US-national cluster) render as one marker instead of stacking. Signals
 * without a resolved centroid are dropped (not plotted at 0,0).
 */
export const groupSignalsByLocation = (
  rows: BiothreatSignal[],
  kind: PrecomputedTab
): LocationGroup[] => {
  const byLocation = new Map<string, BiothreatSignal[]>()
  for (const signal of rows) {
    const latLon = resolveSignalLatLon(signal, kind)
    if (!latLon) continue
    const key = `${latLon.lat},${latLon.lon}`
    const bucket = byLocation.get(key)
    if (bucket) bucket.push(signal)
    else byLocation.set(key, [signal])
  }

  return Array.from(byLocation.entries()).map(([key, signals]) => {
    const ordered = [...signals].sort(
      (a, b) => signalWeight(b, kind) - signalWeight(a, kind)
    )
    // `signals` is never empty (a bucket is only created on first push), so the
    // reduce has a defined result — used instead of `ordered[0]` so the type
    // stays `BiothreatSignal` rather than `BiothreatSignal | undefined`.
    const lead = signals.reduce((best, signal) =>
      signalWeight(signal, kind) > signalWeight(best, kind) ? signal : best
    )
    const diseases = summarizeDiseases(signals)
    const [lat, lon] = key.split(",")
    return {
      key,
      lat: Number(lat),
      lon: Number(lon),
      color: markerColor(lead, kind),
      count: signals.length,
      lead,
      locationLabel: getLocationLabel(lead),
      signals: ordered,
      signalIds: ordered.map(signal => signal.id),
      diseases,
    }
  })
}
